import { ArrowDown, ArrowRight, Network } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { CatalogCta, CatalogHero, CatalogLayout, DataWorkflow } from "../components/catalog";

export function DataDashboardOverview() {
  return (
    <section className="catalog-wrap catalog-section service-overview" id="data-dashboard">
      <div>
        <p className="catalog-kicker">Data dashboard</p>
        <h2>From one recording to the whole project.</h2>
        <p>Connect collection progress, task coverage and reviewed samples in one project view.</p>
        <dl className="service-definitions">
          <dt>Data overview</dt>
          <dd>Track collection volume and quality review outcomes.</dd>
          <dt>Task coverage</dt>
          <dd>Organize episodes by task, scene and modality.</dd>
          <dt>Sample browser</dt>
          <dd>Find reviewed samples by scene, task and annotation.</dd>
        </dl>
      </div>
      <figure className="data-portal-map" aria-label="Data relationship diagram">
        <figcaption>DATA / CONNECTED</figcaption>
        <div className="service-map-root">
          <Network aria-hidden="true" />
          <strong>Tasks & scenes</strong>
          <span>Shared project context</span>
        </div>
        <ArrowDown className="service-flow-arrow" aria-hidden="true" />
        <div className="service-map-tracks">
          <span>Visual records</span>
          <span>Inertial signals</span>
          <span>Task labels</span>
        </div>
        <div className="service-map-records" aria-hidden="true">
          {Array.from(
            {
              length: 8,
            },
            (e, t) => (
              <i key={t} />
            ),
          )}
        </div>
        <div className="service-map-output">
          <strong>Quality review</strong>
          <ArrowRight aria-hidden="true" />
          <strong>Sample browser</strong>
        </div>
        <p>Conceptual workflow. Access and available records depend on the project.</p>
      </figure>
    </section>
  );
}

export function DataPlatformPage() {
  const [e, t] = React.useState("creator");
  const n =
    e === "creator"
      ? [
          ["Define a task", "Specify the goal, scene, equipment and valid-sample requirements."],
          ["Publish and assign", "Set participation, collection instructions and submission rules."],
          ["Track submissions", "Review progress and records associated with the task."],
          ["Review and recollect", "Turn quality feedback into corrections or additional collection."],
        ]
      : [
          ["Choose a task", "Review the goal, eligibility and equipment requirements."],
          ["Accept the assignment", "Confirm consent, scope and submission rules."],
          ["Capture and submit", "Follow the procedure and link each recording to its task."],
          ["Act on feedback", "Check review results and correct or supplement samples."],
        ];
  return (
    <CatalogLayout
      title="Data Platform"
      description="Organize multimodal capture, quality review, dataset versions and traceable batch delivery."
    >
      <div className="catalog-wrap pt-28">
        <Link className="catalog-text-link" to="/solutions/capture">
          Data Collection /
        </Link>
      </div>
      <CatalogHero
        label="Data operations"
        title="Data Platform"
        intro="Turn collection records into reusable datasets. Connect devices, align time, clean and annotate recordings, review quality and publish versions for research and enterprise data delivery."
      />
      <section className="catalog-wrap catalog-section catalog-first">
        <DataWorkflow />
      </section>
      <DataDashboardOverview />
      <section className="catalog-band">
        <div className="catalog-wrap catalog-section">
          <p className="catalog-kicker">Field collection</p>
          <h2>Connect tasks with the people collecting them.</h2>
          <p>Define the task workflow and deployment scope with your project team.</p>
          <div className="catalog-filters" role="group" aria-label="Collection role">
            <button aria-pressed={e === "creator"} onClick={() => t("creator")}>
              Task creator
            </button>
            <button aria-pressed={e === "collector"} onClick={() => t("collector")}>
              Data collector
            </button>
          </div>
          <div className="catalog-features catalog-four">
            {n.map(([r, i]) => (
              <article key={r}>
                <h3>{r}</h3>
                <p>{i}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="catalog-wrap catalog-section">
        <h2>Keep observations and conclusions distinct.</h2>
        <p className="catalog-section-intro">
          Link each episode to its task, device, time and calibration. Derived results preserve their source
          and version without replacing the original recording.
        </p>
        <div className="catalog-features catalog-four">
          {[
            ["Raw observations", "Video, FMG, IMU, tactile signals and robot logs."],
            [
              "Calibration and estimates",
              "Clock alignment, coordinate transforms and estimated hand or tool poses.",
            ],
            ["Annotations and labels", "Task segments, events, human review and robot action labels."],
            ["Dataset releases", "Training, validation and test splits, export manifests and licenses."],
          ].map(([r, i]) => (
            <article key={r}>
              <h3>{r}</h3>
              <p>{i}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="catalog-band">
        <div className="catalog-wrap catalog-section">
          <h2>Agree on the data contract.</h2>
          <div className="catalog-features catalog-four">
            {[
              [
                "Identity and versions",
                "Project, task, episode and device IDs; firmware, calibration, algorithms and dataset versions.",
              ],
              [
                "Time and space",
                "Device and reference clocks, synchronization method and errors, coordinates, units, extrinsics and validity intervals.",
              ],
              [
                "Quality and provenance",
                "Dropped frames, missing data, exceptions and review results; identify measurements, estimates and human labels.",
              ],
              [
                "Rights and evaluation",
                "Collection consent, usage scope and access control; separate evaluation data by participant, scene or date.",
              ],
            ].map(([r, i]) => (
              <article key={r}>
                <h3>{r}</h3>
                <p>{i}</p>
              </article>
            ))}
          </div>
          <p className="catalog-note">
            This is a project data-contract framework. Final fields, export formats and platform functions
            depend on the selected version and sample validation.
          </p>
        </div>
      </section>
      <section className="catalog-wrap catalog-section" id="data-delivery">
        <h2>Scale collection with measurable acceptance.</h2>
        <p className="catalog-section-intro">
          Use a pilot batch to establish quality and processing cost before planning people, devices, scenes
          and delivery batches.
        </p>
        <div className="catalog-two">
          <article>
            <h3>Every delivery batch</h3>
            <ul className="catalog-checklist">
              <li>Raw and processed data with a field dictionary and reading instructions.</li>
              <li>File manifests, versions, checksums and calibration files.</li>
              <li>Accepted-sample counts, coverage distributions and quality reports.</li>
              <li>Rejected-sample reasons, rework records and usage-rights documentation.</li>
            </ul>
          </article>
          <article>
            <h3>Before collection starts</h3>
            <ul className="catalog-checklist">
              <li>Define accepted episodes, duration and completed-task samples.</li>
              <li>Set timing, missing-data and annotation-consistency thresholds.</li>
              <li>Agree on task, object, environment, participant and failure coverage.</li>
              <li>Confirm ownership, U.S. storage, retention and third-party processing permissions.</li>
            </ul>
          </article>
        </div>
        <div className="catalog-callout">
          <h3>Use failures to plan the next batch.</h3>
          <p>
            Trace failure states to recollection requirements. Release corrective demonstrations as a separate
            version and evaluate against a fixed test set, while retaining broad scenario coverage.
          </p>
        </div>
        <div className="catalog-actions">
          <Link className="catalog-text-link" to="/developers">
            Device and algorithm integration →
          </Link>
          <Link className="catalog-text-link" to="/training">
            Training and evaluation →
          </Link>
        </div>
      </section>
      <CatalogCta />
    </CatalogLayout>
  );
}
